/* ============================================================================
   nav-prefetch.js - idle + hover prefetch for likely next pages
   ============================================================================ */

import { ASSET_VERSION } from "./asset-version.js?v=20261007m";
import { warmFaqCmsCache } from "./site-copy.js?v=20261007m";

const v = `?v=${ASSET_VERSION}`;

const docPrefetched = new Set();
const modulePrefetched = new Set();
const modulePreloadStarted = new Set();

/** App pages worth prefetching (not admin).
 * Absolute `/js/…` + @vite-ignore keeps these out of page bundles (P2);
 * runtime still loads the live unbundled modules. */
const PAGE_MODULE_LOADERS = {
  dashboard: () => import(/* @vite-ignore */ `/js/pages/dashboard.js?v=${ASSET_VERSION}`),
  roadmap: () => import(/* @vite-ignore */ `/js/pages/roadmap.js?v=${ASSET_VERSION}`),
  faq: () => import(/* @vite-ignore */ `/js/pages/faq.js?v=${ASSET_VERSION}`),
  profile: () => import(/* @vite-ignore */ `/js/pages/profile.js?v=${ASSET_VERSION}`),
  "my-assets": () => import(/* @vite-ignore */ `/js/pages/my-assets.js?v=${ASSET_VERSION}`),
  "members-pantry": () => import(/* @vite-ignore */ `/js/pages/members-pantry.js?v=${ASSET_VERSION}`),
  "story-map": () => import(/* @vite-ignore */ `/js/pages/story-map.js?v=${ASSET_VERSION}`),
  "story-bible": () => import(/* @vite-ignore */ `/js/pages/story-bible.js?v=${ASSET_VERSION}`),
  "spice-rack": () => import(/* @vite-ignore */ `/js/pages/spice-rack.js?v=${ASSET_VERSION}`),
  contests: () => import(/* @vite-ignore */ `/js/pages/contests.js?v=${ASSET_VERSION}`),
  "featured-outfits": () => import(/* @vite-ignore */ `/js/pages/featured-outfits-app.js?v=${ASSET_VERSION}`),
  cookbook: () => import(/* @vite-ignore */ `/js/pages/cookbook.js?v=${ASSET_VERSION}`),
  "modify-combos": () => import(/* @vite-ignore */ `/js/pages/modify-combos.js?v=${ASSET_VERSION}`),
};

/** Idle warm targets per current page. */
const IDLE_NEIGHBORS = {
  dashboard: ["faq", "roadmap"],
  roadmap: ["dashboard", "faq"],
  faq: ["dashboard", "profile"],
  profile: ["dashboard", "my-assets"],
  "my-assets": ["dashboard", "profile"],
  "members-pantry": ["dashboard", "faq"],
  "story-map": ["dashboard", "cookbook"],
  "featured-outfits": ["dashboard", "faq"],
  cookbook: ["dashboard", "modify-combos"],
  "modify-combos": ["dashboard", "featured-outfits"],
};

const HREF_TO_PAGE = {
  "dashboard.html": "dashboard",
  "roadmap.html": "roadmap",
  "faq.html": "faq",
  "profile.html": "profile",
  "my-assets.html": "my-assets",
  "members-pantry.html": "members-pantry",
  "story-map.html": "story-map",
  "story-bible.html": "story-bible",
  "spice-rack.html": "spice-rack",
  "contests.html": "contests",
  "contest.html": "contest",
  "featured-outfits.html": "featured-outfits",
  "cookbook.html": "cookbook",
  "modify-combos.html": "modify-combos",
};

const LANDING_LEGAL_HREFS = ["faq.html", "terms.html", "privacy.html", "asset-usage.html"];
const LEGAL_FILES_BY_ID = {
  faq: "faq.html",
  terms: "terms.html",
  privacy: "privacy.html",
  "asset-usage": "asset-usage.html",
};
const LEGAL_LINK_SELECTOR =
  'a[href="faq.html"], a[href="terms.html"], a[href="privacy.html"], a[href="asset-usage.html"], a[href*="/faq.html"], a[href*="/terms.html"], a[href*="/privacy.html"], a[href*="/asset-usage.html"]';

/** Prefer a short deadline. Landing animations often never go idle. */
export function scheduleIdle(fn, timeoutMs = 1200) {
  const run = () => {
    try {
      fn();
    } catch (_) {}
  };
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(run, { timeout: timeoutMs });
  } else {
    setTimeout(run, Math.min(timeoutMs, 400));
  }
}

function scheduleSoon(fn, delayMs = 200) {
  const run = () => {
    try {
      fn();
    } catch (_) {}
  };
  if (document.readyState === "complete") {
    setTimeout(run, delayMs);
  } else {
    window.addEventListener(
      "load",
      () => {
        setTimeout(run, delayMs);
      },
      { once: true }
    );
  }
}

function pageIdFromHref(href) {
  const raw = String(href || "").trim();
  if (!raw) return "";
  const path = raw.split(/[?#]/)[0];
  const file = path.split("/").pop() || "";
  return HREF_TO_PAGE[file] || "";
}

function hrefForPageId(pageId) {
  if (!pageId || pageId.startsWith("admin")) return "";
  return `${pageId}.html`;
}

function legalIdFromHref(href) {
  try {
    const path = new URL(href, location.href).pathname;
    const match = path.match(/\/(terms|privacy|asset-usage|faq)\.html$/i);
    return match ? match[1].toLowerCase() : "";
  } catch (_) {
    return "";
  }
}

/** Fetch HTML so Turbo / full navigation hits cache sooner. */
export function prefetchDocument(href) {
  const url = String(href || "").trim();
  if (!url || url.startsWith("#") || /^https?:\/\//i.test(url)) return;
  const key = url.split("#")[0];
  if (docPrefetched.has(key)) return;
  docPrefetched.add(key);

  const absolute = new URL(key, location.href).href;
  fetch(absolute, { credentials: "same-origin", priority: "low" }).catch(() => {});
}

function preloadModuleScript(src) {
  if (!src || modulePreloadStarted.has(src)) return;
  modulePreloadStarted.add(src);
  const link = document.createElement("link");
  link.rel = "modulepreload";
  link.href = src;
  document.head.appendChild(link);
}

/** Dynamic-import a page module once. */
export function prefetchAppPageModule(pageId) {
  const id = String(pageId || "").trim();
  if (!id || modulePrefetched.has(id)) return;
  const loader = PAGE_MODULE_LOADERS[id];
  if (!loader) return;
  modulePrefetched.add(id);
  loader().catch(() => {
    modulePrefetched.delete(id);
  });
}

function prefetchPageBundle(pageId) {
  if (!pageId) return;
  const href = hrefForPageId(pageId);
  if (href) prefetchDocument(href);
  prefetchAppPageModule(pageId);
  if (pageId === "faq") {
    void warmFaqCmsCache("portal").catch(() => {});
  }
}

function prefetchLegalOverlay(id = "") {
  void import(`./legal-lightbox.js${v}`)
    .then((mod) => {
      if (id) mod.prefetchLegalDocs?.([id]);
      else mod.prefetchLegalDocs?.();
    })
    .catch(() => {});
}

function hasSignedInHint() {
  try {
    return Boolean(sessionStorage.getItem("ccj_last_uid"));
  } catch (_) {
    return false;
  }
}

/** First visit to app HTML from landing: prime Turbo + shell once. */
export function prefetchAppShellScripts() {
  preloadModuleScript("js/turbo-init.js");
  preloadModuleScript("js/vendor/turbo.es2017-esm.js");
  preloadModuleScript(`js/app-shell.js${v}`);
}

/** Landing / cover home: legal overlays + FAQ, and vault if they already signed in. */
export function scheduleLandingFaqPrefetch() {
  scheduleSoon(() => {
    for (const href of LANDING_LEGAL_HREFS) prefetchDocument(href);
    prefetchAppShellScripts();
    prefetchAppPageModule("faq");
    prefetchLegalOverlay();
    void warmFaqCmsCache("portal").catch(() => {});
    if (hasSignedInHint()) {
      prefetchDocument("dashboard.html");
      prefetchAppPageModule("dashboard");
    }
  }, 180);
}

/** Wire hover/focus prefetch on a single nav anchor. */
function wireNavAnchor(anchor) {
  if (!anchor || anchor.dataset.prefetchWired === "1") return;
  const href = anchor.getAttribute("href") || "";
  const pageId = anchor.dataset.page || pageIdFromHref(href);
  if (!pageId || !PAGE_MODULE_LOADERS[pageId]) return;

  anchor.dataset.prefetchWired = "1";
  if (!anchor.hasAttribute("data-turbo-prefetch")) {
    anchor.setAttribute("data-turbo-prefetch", "");
  }

  const warm = () => prefetchPageBundle(pageId);
  anchor.addEventListener("mouseenter", warm, { once: true, passive: true });
  anchor.addEventListener("focus", warm, { once: true });
}

function wireLegalAnchor(anchor) {
  if (!anchor || anchor.dataset.legalPrefetchWired === "1") return;
  const href = anchor.getAttribute("href") || "";
  const legalId = legalIdFromHref(href);
  if (!legalId) return;
  anchor.dataset.legalPrefetchWired = "1";
  const warm = () => {
    const file = LEGAL_FILES_BY_ID[legalId] || `${legalId}.html`;
    prefetchDocument(file);
    prefetchLegalOverlay(legalId);
    if (legalId === "faq") {
      prefetchAppShellScripts();
      prefetchAppPageModule("faq");
    }
  };
  anchor.addEventListener("mouseenter", warm, { once: true, passive: true });
  anchor.addEventListener("focus", warm, { once: true });
}

/** App sidebar + in-content links to core member pages. */
export function wireAppNavPrefetch(root = document) {
  root.querySelectorAll(".nav__item[href], a[href$='.html']").forEach((anchor) => {
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
    wireNavAnchor(anchor);
    wireLegalAnchor(anchor);
  });
}

/** After paint, warm likely next pages for the active app route. */
export function scheduleIdleNeighborPrefetch(pageId) {
  const neighbors = IDLE_NEIGHBORS[pageId];
  if (!neighbors?.length) return;

  scheduleIdle(() => {
    for (const nextId of neighbors.slice(0, 4)) {
      if (nextId === pageId) continue;
      prefetchPageBundle(nextId);
    }
    prefetchLegalOverlay();
  }, 900);
}

export function initNavPrefetch() {
  const pageId = document.body?.dataset?.page || "";
  const isLanding =
    pageId === "landing" ||
    document.body.classList.contains("landing");

  if (isLanding) {
    scheduleLandingFaqPrefetch();
    document.querySelectorAll(LEGAL_LINK_SELECTOR).forEach((anchor) => {
      wireLegalAnchor(anchor);
    });
    return;
  }

  if (!pageId || pageId.startsWith("admin")) return;

  wireAppNavPrefetch();
  scheduleIdleNeighborPrefetch(pageId);
}
